import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { fetchData } from '../../../apiService';
import PrintIcon from '@mui/icons-material/Print';
import PurchaseOrderBill from './Bill';

const PurchaseOrderDetails = () => {
    const { id } = useParams();
    const [selectedItems, setSelectedItems] = useState([]);
    const { isAuthenticated, token, logout, companyId } = useAuth();
    const [formData, setFormData] = useState({});
    const [companyData, setCompanyData] = useState({});
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        fetchData('PurchaseOrderApi', `/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data.PurchaseOrder);
                    setSelectedItems(response.response_data.PurchaseOrderDetails);
                }
            })
            .catch((error) => console.error('Error fetching Purchase Order Details:', error));

        fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setCompanyData(response.response_data);
                }
            })
            .catch((error) => console.error('Error fetching company details:', error));
    }, [id]);

    const handlePrint = () => {
        const printContent = document.getElementById('purchase-order-print-area').innerHTML;
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><title>Purchase Order</title>
            <style>
                @page { margin: 8mm; size: A4; }
                body { margin: 0; font-family: Arial, sans-serif; }
                * { box-sizing: border-box; }
            </style>
        </head><body>${printContent}</body></html>`);
        doc.close();
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const filteredData = selectedItems.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // ── shared styles ──────────────────────────────────────────────────────────
    const inp = { border: "1px solid rgba(0,0,0,0.12)", borderRadius: "7px", fontSize: "12px", height: "28px", width: "100%", padding: "0 8px", outline: "none", backgroundColor: "#f8fafa", boxSizing: "border-box" };
    const inpR = { ...inp, textAlign: "right" };
    const card = { backgroundColor: "#ffffff", borderRadius: "10px", boxShadow: "0 2px 10px rgba(0,0,0,0.07)", border: "1px solid rgba(0,0,0,0.06)", marginBottom: "10px", overflow: "hidden" };
    const cardHead = (color = "#2dd4bf") => ({ display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px", borderBottom: "1px solid rgba(0,0,0,0.06)", backgroundColor: `${color}18`, fontSize: "10px", fontWeight: 700, color: "#1a2e2e", textTransform: "uppercase", letterSpacing: "0.5px" });
    const cardBody = { padding: "10px 12px" };
    const lbl = { fontSize: "9.5px", fontWeight: 700, color: "#5a7a7a", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "3px" };

    return (
        <div style={{ fontFamily: "Segoe UI, sans-serif", fontSize: "12px", width: "100%", padding: "6px 6px 6px", boxSizing: "border-box", backgroundColor: "#f0f4f4" }}>

            {/* ── Page header card ── */}
            <div style={{ ...card, marginBottom: "10px", overflow: "visible" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", flexWrap: "wrap", gap: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontSize: "15px", color: "#2dd4bf" }}>🛒</span>
                        <span style={{ fontWeight: 800, fontSize: "13px", color: "#1a2e2e", fontFamily: "Inter, sans-serif" }}>Purchase Order — Details</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "11px", fontWeight: 600, color: "#5a7a7a" }}>Supplier:</span>
                        <input type="text" value={formData.SupplierName || ''} readOnly style={{ ...inp, width: "220px" }} />
                    </div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <button onClick={handlePrint}
                            style={{ height: "28px", padding: "0 14px", borderRadius: "7px", border: "none", backgroundColor: "#1E3A8A", color: "#fff", fontWeight: 700, fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                            <PrintIcon style={{ fontSize: 14 }} /> Print
                        </button>
                        <Link to="/PurchaseOrderIndex" onClick={() => localStorage.setItem("shouldReload", "true")}
                            style={{ height: "28px", padding: "0 14px", borderRadius: "7px", border: "1px solid rgba(0,0,0,0.12)", backgroundColor: "#fff", color: "#5a7a7a", fontWeight: 600, fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", textDecoration: "none" }}>
                            ← Back
                        </Link>
                    </div>
                </div>
            </div>

            {/* ── Bill details card ── */}
            <div style={card}>
                <div style={cardHead()}>📋 Bill Details</div>
                <div style={{ ...cardBody, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px" }}>
                    <div><label style={lbl}>Bill Date</label><input type="date" value={formatDateForInput(formData.BillDate)} readOnly style={inp} /></div>
                    <div><label style={lbl}>Bill Number</label><input type="text" value={formData.BillNumber || ''} readOnly style={inp} /></div>
                    <div><label style={lbl}>Round Off</label><input type="number" value={formData.RoundOff || ''} readOnly style={inpR} /></div>
                    <div><label style={lbl}>Total</label><input type="number" value={formData.TotalAmount || ''} readOnly style={{ ...inpR, fontWeight: 700, color: "#0D47A1" }} /></div>
                </div>
            </div>

            {/* ── Summary bar ── */}
            <div style={card}>
                <div style={{ ...cardBody, display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "8px" }}>
                    {[["Amount", formData.Amount], ["Discount", formData.DiscountAmount], ["Taxable", formData.TaxableAmount], ["CGST", formData.CGSTAmount], ["SGST", formData.SGSTAmount], ["IGST", formData.IGSTAmount]].map(([label, val]) => (
                        <div key={label} style={{ fontSize: "11px", color: "#5a7a7a" }}>{label}: <strong style={{ color: "#B71C1C" }}>{val}</strong></div>
                    ))}
                </div>
            </div>

            {/* ── Items table card ── */}
            <div style={card}>
                <div style={cardHead("#1F2A38")}>📦 Items ({filteredData.length})</div>
                <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                        <thead style={{ backgroundColor: "#1F2A38", color: "#fff", position: "sticky", top: 0 }}>
                            <tr>
                                {["SL.No", "Item", "Qty", "Rate", "Amount", "Disc%", "Disc₹", "Taxable", "GST%", "Bal.Qty", "Total"].map((h, i) => (
                                    <th key={i} style={{ padding: "6px 4px", textAlign: i > 1 ? "right" : "left", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((item, index) => (
                                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                                    <td style={{ padding: "4px 6px", textAlign: "center" }}>{index + 1}</td>
                                    <td style={{ padding: "4px", minWidth: "160px", fontWeight: 600 }}>{item.ItemName}</td>
                                    <td style={{ padding: "2px", textAlign: "right" }}><input value={item.Quantity} readOnly style={{ ...inpR, height: "24px", width: "70px", borderRadius: "4px" }} /></td>
                                    <td style={{ padding: "2px", textAlign: "right" }}><input value={item.Rate || 0} readOnly style={{ ...inpR, height: "24px", width: "100px", borderRadius: "4px" }} /></td>
                                    <td style={{ padding: "2px", textAlign: "right" }}><input value={item.Amount || 0} readOnly style={{ ...inpR, height: "24px", width: "100px", borderRadius: "4px" }} /></td>
                                    <td style={{ padding: "2px", textAlign: "right" }}><input value={item.DiscountPercentage || 0} readOnly style={{ ...inpR, height: "24px", width: "70px", borderRadius: "4px" }} /></td>
                                    <td style={{ padding: "2px", textAlign: "right" }}><input value={item.DiscountAmount || 0} readOnly style={{ ...inpR, height: "24px", width: "100px", borderRadius: "4px" }} /></td>
                                    <td style={{ padding: "2px", textAlign: "right" }}><input value={item.TaxableAmount || 0} readOnly style={{ ...inpR, height: "24px", width: "100px", borderRadius: "4px" }} /></td>
                                    <td style={{ padding: "2px", textAlign: "right" }}><span style={{ color: "darkmagenta", fontSize: "11px" }}>{item.GSTRate || 0}%</span></td>
                                    <td style={{ padding: "2px", textAlign: "right", width: "70px" }}><span style={{ color: "#2e7d32", fontWeight: "600", fontSize: "11px" }}>{item.BalanceQuantity || 0}</span></td>
                                    <td style={{ padding: "2px", textAlign: "right" }}><input value={item.TotalAmount || 0} readOnly style={{ ...inpR, height: "24px", width: "100px", borderRadius: "4px", fontWeight: 700, color: "#b45309" }} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Remarks card ── */}
            {formData.Remarks && (
                <div style={card}>
                    <div style={cardHead("#f59e0b")}>📝 Remarks</div>
                    <div style={cardBody}>
                        <span style={{ fontSize: "12px" }}>{formData.Remarks}</span>
                    </div>
                </div>
            )}

            {/* Hidden Print Area */}
            <div style={{ display: 'none' }}>
                <div id="purchase-order-print-area">
                    <PurchaseOrderBill labelData={{
                        PurchaseOrder: {
                            ...formData,
                            CompanyName: companyData.Name,
                            CompanyAddressLine: `${companyData.AddressLine1 || ''} ${companyData.AddressLine2 || ''}`.trim(),
                            CompanyContactNumber1: companyData.ContactNumber1,
                            CompanyGSTNumber: companyData.GSTNumber,
                        },
                        PurchaseOrderDetails: selectedItems
                    }} />
                </div>
            </div>
        </div>
    );
};

export default PurchaseOrderDetails;
